import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  BadgeOutlined,
  EmailOutlined,
  InfoOutlined,
  PersonOutline,
  PhoneOutlined,
} from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { type MouseEvent, type ReactNode, useState } from 'react';

interface DriverInfoPopoverProps {
  name?: string | null;
  cpf?: string | null;
  phone?: string | null;
  email?: string | null;
}

const formatCpf = (value?: string | null) => {
  const digits = value?.replace(/\D/g, '') ?? '';
  if (digits.length !== 11) return value?.trim() || t('locationTransport.notProvided');

  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatPhone = (value?: string | null) => {
  const rawDigits = value?.replace(/\D/g, '') ?? '';
  const digits =
    rawDigits.startsWith('55') && rawDigits.length > 11
      ? rawDigits.slice(2)
      : rawDigits;

  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return value?.trim() || t('locationTransport.notProvided');
};

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box sx={{ color: '#60a5fa', display: 'flex', mt: 0.2 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: '#94a3b8', display: 'block', lineHeight: 1.2 }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#e2e8f0',
            fontWeight: 650,
            overflowWrap: 'anywhere',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function DriverInfoPopover({
  name,
  cpf,
  phone,
  email,
}: DriverInfoPopoverProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!name && !cpf && !phone && !email) return null;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={t('locationTransport.driverInfo')}>
        <IconButton
          size="small"
          aria-label={t('locationTransport.viewDriverInfo')}
          aria-describedby={open ? 'driver-info-popover' : undefined}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={handleOpen}
          sx={{
            width: 30,
            height: 30,
            color: '#93c5fd',
            border: '1px solid rgba(96, 165, 250, 0.3)',
            backgroundColor: 'rgba(37, 99, 235, 0.1)',
            '&:hover': { backgroundColor: 'rgba(37, 99, 235, 0.22)' },
          }}
        >
          <InfoOutlined sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Popover
        id="driver-info-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            onClick: (event) => event.stopPropagation(),
            sx: {
              mt: 0.75,
              width: 300,
              maxWidth: 'calc(100vw - 24px)',
              p: 2,
              color: '#e2e8f0',
              background:
                'linear-gradient(145deg, rgba(15, 23, 42, 0.98), rgba(30, 41, 59, 0.98))',
              border: '1px solid rgba(96, 165, 250, 0.24)',
              borderRadius: 2,
              boxShadow: '0 18px 45px rgba(2, 6, 23, 0.42)',
            },
          },
        }}
      >
        <Stack spacing={1.5}>
          <Box>
            <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
              {t('locationTransport.driverInfo')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              {t('locationTransport.contactDetails')}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.16)' }} />
          <InfoRow
            icon={<PersonOutline sx={{ fontSize: 19 }} />}
            label={t('locationTransport.fullName')}
            value={name?.trim() || t('locationTransport.notProvided')}
          />
          <InfoRow
            icon={<BadgeOutlined sx={{ fontSize: 19 }} />}
            label="CPF"
            value={formatCpf(cpf)}
          />
          <InfoRow
            icon={<PhoneOutlined sx={{ fontSize: 19 }} />}
            label={t('locationTransport.phone')}
            value={formatPhone(phone)}
          />
          {email?.trim() && (
            <InfoRow
              icon={<EmailOutlined sx={{ fontSize: 19 }} />}
              label={t('locationTransport.email')}
              value={email.trim()}
            />
          )}
        </Stack>
      </Popover>
    </>
  );
}
